"use client";

import {
  Button,
  FormInput,
  FormPrice,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { WalletAccountItem } from "@/modules/wallet/presentation/api/client";
import { formatPrice } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { walletApi } from "../api/client";
import { useCreateRefundMutation } from "../api/refund-queries";

const CURRENCY_OPTIONS = [
  { value: "LAK", label: "LAK" },
  { value: "THB", label: "THB" },
  { value: "USD", label: "USD" },
];

const REFERENCE_TYPE_OPTIONS = [
  { value: "none", label: "ບໍ່ອ້າງອີງ" },
  { value: "order", label: "ຄຳສັ່ງຊື້" },
  { value: "job", label: "ງານ" },
];

const OWNER_TYPE_LABELS: Record<string, string> = {
  customer: "ລູກຄ້າ",
  shop: "ຮ້ານຄ້າ",
  mechanic: "ຊ່າງ",
  platform: "ແພລດຟອມ",
};

function walletOptionLabel(w: WalletAccountItem): string {
  const type = OWNER_TYPE_LABELS[w.ownerType] ?? w.ownerType;
  const name = w.ownerName ?? w.ownerId ?? w.id.slice(0, 8);
  return `${name} (${type}) · ${formatPrice(w.balance, w.currency)}`;
}

const RefundCreateSchema = z.object({
  walletId: z
    .string()
    .min(1, "ກະລຸນາເລື່ອກກະເປົາເງິນ")
    .refine((v) => /^[0-9a-f-]{36}$/i.test(v), {
      message: "ກະລຸນາເລື່ອກກະເປົາເງິນ",
    }),
  amount: z.coerce.number().min(0.01, "ຈຳນວນຕ້ອງບໍ່ຕ່ຳກວ່າ 0.01"),
  currency: z.enum(["LAK", "THB", "USD"]),
  reason: z.string().max(500).optional(),
  referenceType: z.enum(["none", "order", "job"]),
  referenceId: z.string().optional(),
});

type RefundCreateValues = z.infer<typeof RefundCreateSchema>;

const LIMIT = 20;

function walletLookupQueryFn(args: {
  search: string;
  pageParam: number;
}): Promise<{ items: WalletAccountItem[]; nextPage: number | null }> {
  const offset = (args.pageParam - 1) * LIMIT;
  const filters = args.search
    ? [
        {
          field: "ownerId" as const,
          op: "contains" as const,
          value: args.search,
        },
      ]
    : undefined;
  return walletApi
    .listAccounts({ limit: LIMIT, offset, filters })
    .then((r) => ({
      items: r.data,
      nextPage:
        offset + r.data.length < r.meta.total ? args.pageParam + 1 : null,
    }));
}

function walletPreloadQueryFn(id: string): Promise<WalletAccountItem | null> {
  return walletApi
    .listAccounts({
      limit: 1,
      offset: 0,
      filters: [{ field: "id", op: "eq", value: id }],
    })
    .then((r) => r.data[0] ?? null);
}

export function RefundCreatePage() {
  const nav = useNavigate({ from: "/app/wallet/refunds/create" });
  const createMutation = useCreateRefundMutation();

  const methods = RHF.useForm<RefundCreateValues>({
    resolver: zodResolver(
      RefundCreateSchema,
    ) as RHF.Resolver<RefundCreateValues>,
    defaultValues: {
      walletId: "",
      amount: 0,
      currency: "LAK",
      reason: "",
      referenceType: "none",
      referenceId: "",
    },
  });

  const selectedWalletId = RHF.useWatch({
    control: methods.control,
    name: "walletId",
    defaultValue: "",
  });

  useEffect(() => {
    if (!selectedWalletId) return;
    walletPreloadQueryFn(selectedWalletId).then((w) => {
      if (w) methods.setValue("currency", w.currency as "LAK" | "THB" | "USD");
    });
  }, [selectedWalletId, methods]);

  const onSubmit = async (vals: RefundCreateValues) => {
    try {
      await createMutation.mutateAsync({
        walletId: vals.walletId,
        amount: vals.amount,
        currency: vals.currency,
        reason: vals.reason?.trim() || undefined,
        referenceType:
          vals.referenceType === "order" || vals.referenceType === "job"
            ? vals.referenceType
            : undefined,
        referenceId: vals.referenceId?.trim() || undefined,
      });
      nav({ to: "/app/wallet/refunds" });
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ຄືນເງິນ", to: "/app/wallet/refunds" },
            { label: "ສ້າງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ສ້າງຄືນເງິນ</h2>
            <p className="text-muted-foreground">
              ເລື່ອກກະເປົາ ຈຳນວນເງິນ ແລະ ອ້າງອີງ (order/job) ຖ້າມີ
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/wallet/refunds" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div className="mt-6 rounded-xl border bg-card p-6">
          <FormRoot<RefundCreateValues>
            methods={methods}
            onSubmit={onSubmit}
            className="space-y-4"
          >
            <FormInfiniteCombobox<WalletAccountItem>
              name="walletId"
              label="ກະເປົາເງິນ (ເຈົ້າຂອງທີ່ຈະໄດ້ຮັບຄືນ)"
              requiredMark
              queryKey={["wallet", "refund", "accounts", "lookup"]}
              queryFn={walletLookupQueryFn}
              preloadQueryFn={walletPreloadQueryFn}
              getLabel={walletOptionLabel}
              getValue={(item) => item.id}
              placeholder="ຄົ້ນຫາ ຫຼື ເລື່ອກກະເປົາ..."
              clearable
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormPrice
                name="amount"
                requiredMark
                label="ຈຳນວນເງິນ"
                placeholder="0"
                currencySymbol="K"
              />
              <FormSelect
                name="currency"
                label="ສະກຸນເງິນ"
                options={CURRENCY_OPTIONS}
              />
            </div>

            <FormTextarea
              name="reason"
              label="ເຫດຜົນ (ບໍ່ບັງຄັບ)"
              placeholder="ອະທິບາຍເຫດຜົນການຄືນເງິນ"
              rows={3}
              className="resize-none"
            />

            <div className="rounded-lg border bg-muted/30 p-4">
              <p className="mb-3 text-muted-foreground text-sm">
                ອ້າງອີງຄຳສັ່ງຊື້ ຫຼື ງານ (ຖ້າມີ)
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormSelect
                  name="referenceType"
                  label="ປະເພດອ້າງອີງ"
                  options={REFERENCE_TYPE_OPTIONS}
                />
                <FormInput
                  name="referenceId"
                  label="ລະຫັດ (UUID)"
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => nav({ to: "/app/wallet/refunds" })}
              >
                ຍົກເລີກ
              </Button>
              <Button type="submit" isLoading={createMutation.isPending}>
                ບັນທຶກ
              </Button>
            </div>
          </FormRoot>
        </div>
      </Main>
    </>
  );
}
